import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ApiProvider } from './contexts/ApiContext';
import Layout from './components/Layout';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import CreateEstimate from './pages/CreateEstimate';
import AddProduct from './pages/AddProduct';
import EstimateDetails from './pages/EstimateDetails';
import CartDetails from './pages/CartDetails';
import OrderDetails from './pages/OrderDetails';

// Admin Components
import AdminRoute from './components/admin/AdminRoute';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, userValidationError } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (userValidationError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4">
          <div className="text-center">
            <div className="bg-red-100 w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Account Not Activated</h2>
            <p className="text-gray-600 mb-6">{userValidationError}</p>
            <button
              onClick={() => 
              {
                window.location = "/signin"
              }
              }
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  // Check if this is an admin route based on URL path
  const isAdminRoute = window.location.pathname.split('/')[1] === 'admin';
  const isRegisterRoute = window.location.pathname.split('/')[1] === 'register';
  const isLoginRoute = window.location.pathname.split('/')[1] === 'login';
  const isDealerRoute = window.location.pathname.split('/')[1] === 'dealer';

  // Handle legacy admin routes with query params
  if (isAdminRoute || isRegisterRoute || isLoginRoute || isDealerRoute) {
    const params = new URLSearchParams(window.location.search);
    const authKey = params.get('auth_key');
    const uid = params.get('uid');

    if (isAdminRoute) {
      return (
        <div className="flex w-full min-h-screen bg-gray-100">
          <AdminRoute authKey={authKey} uid={uid} />
        </div>
      );
    }

    if (isRegisterRoute || isLoginRoute || isDealerRoute) {
      // Redirect old routes to new auth system
      window.location.href = '/signin';
      return null;
    }
  }

  // Modern React Router for dealer portal
  return (
    <AuthProvider>
      <ApiProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route 
                path="/signin" 
                element={
                  <PublicRoute>
                    <SignIn />
                  </PublicRoute>
                } 
              />
              <Route 
                path="/signup" 
                element={
                  <PublicRoute>
                    <SignUp />
                  </PublicRoute>
                } 
              />

              {/* Protected Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/estimates" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/create-estimate" 
                element={
                  <ProtectedRoute>
                    <CreateEstimate />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/edit-estimate/:id" 
                element={
                  <ProtectedRoute>
                    <CreateEstimate />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-product" 
                element={
                  <ProtectedRoute>
                    <AddProduct />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/estimate-details/:id" 
                element={
                  <ProtectedRoute>
                    <EstimateDetails />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/cart-details/:id" 
                element={
                  <ProtectedRoute>
                    <CartDetails />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/order-details/:id" 
                element={
                  <ProtectedRoute>
                    <OrderDetails />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <div className="p-6">
                      <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
                      <p className="text-gray-600 mt-2">Profile management coming soon...</p>
                    </div>
                  </ProtectedRoute>
                } 
              />

              {/* Redirect root to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              
              {/* Catch all route */}
              <Route 
                path="*" 
                element={
                  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                      <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                      <p className="text-gray-600 mb-6">Page not found</p>
                      <a 
                        href="/dashboard" 
                        className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                      >
                        Go to Dashboard
                      </a>
                    </div>
                  </div>
                } 
              />
            </Routes>
          </div>
        </Router>
      </ApiProvider>
    </AuthProvider>
  );
}

export default App;